import { useEffect, useState } from 'react';
import { ConfirmModal } from '../../../components/ConfirmModal';
import { Checkbox } from '../../../components/Checkbox';
import { TextInput } from '../../../components/TextInput';
import type { BillingContact, BillingContactKind } from '../../../types/adminBilling';



export interface BillingContactDraft {
  email: string;
  name: string;
  kind: BillingContactKind;
  isActive: boolean;
}

interface BillingContactModalProps {
  isOpen: boolean;
  /** The contact being edited, or null when adding a new one. */
  contact: BillingContact | null;
  saving: boolean;
  error: string | null;
  onSave: (draft: BillingContactDraft) => void;
  onClose: () => void;
}

/**
 * Add or edit one accounts payable contact.
 *
 * Reopening resets the fields from `contact`, keyed on which contact it is: without that, editing
 * one contact and then opening another would show the first one's address over the second one's
 * name, and the save would quietly write the wrong values.
 */
export const BillingContactModal = ({
  isOpen,
  contact,
  saving,
  error,
  onSave,
  onClose,
}: BillingContactModalProps) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  // Always To: see the note where the picker used to be.
  const kind: BillingContactKind = 'To';
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (!isOpen) return;
    setEmail(contact?.email ?? '');
    setName(contact?.name ?? '');
    setIsActive(contact?.isActive ?? true);
  }, [isOpen, contact]);

  return (
    <ConfirmModal
      isOpen={isOpen}
      title={contact ? 'Edit billing contact' : 'Add a billing contact'}
      message="This is the accounts payable address, not the coach's. Invoices and payment reminders go here."
      confirmText={saving ? 'Saving...' : 'Save contact'}
      busy={saving}
      onConfirm={() => onSave({ email, name, kind, isActive })}
      onClose={onClose}
    >
      <div className="billing-form">
        <TextInput
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={saving}
          data-testid="billing-contact-email"
        />
        <TextInput
          label="Name (optional)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={saving}
          data-testid="billing-contact-name"
        />
        {/*
          No kind to choose. Stripe emails the customer's one address, so a Cc here would go
          nowhere - only this system's own reminders ever used it, and those are gone. Every
          contact is created as a To, and the first active one becomes the Stripe customer's email.
        */}
        {/*
          Offered on an edit only. A new contact is created active, and a checkbox that starts
          ticked and does nothing useful is just a way to create a contact nobody will ever email.
        */}
        {contact && (
          <Checkbox
            label="Active"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            disabled={saving}
            data-testid="billing-contact-active"
          />
        )}
        {error && (
          <p className="billing-form__error" role="alert" data-testid="billing-contact-error">
            {error}
          </p>
        )}
      </div>
    </ConfirmModal>
  );
};
